import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, Bot, Check, Code2, Copy, KeyRound, ShieldCheck, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ExperimentalBadge } from "@/components/experimental-badge";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/api-docs")({
  head: () =>
    seo({
      title: "API Documentation & MCP Server Setup | JobCues",
      description:
        "Integrate JobCues programmatically with REST endpoints, or connect Claude, Cursor, and Windsurf to the JobCues Model Context Protocol server.",
      path: "/api-docs",
      type: "article",
    }),
  component: ApiDocsComponent,
});

function ApiDocsComponent() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copySnippet = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    toast.success("Snippet copied to clipboard!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const originUrl = typeof window !== "undefined" ? window.location.origin : "https://jobcues.com";

  const mcpEndpointUrl = `${originUrl}/api/mcp`;

  const claudeConfigSnippet = JSON.stringify(
    {
      mcpServers: {
        "super-resume": {
          url: mcpEndpointUrl,
          headers: {
            Authorization: "Bearer sk_live_your_api_key_here",
          },
        },
      },
    },
    null,
    2,
  );

  const curlResumeTailor = `curl -X POST "${mcpEndpointUrl}" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "tool": "resume_tailor",
    "job_title": "Senior Frontend Engineer",
    "job_description": "We are seeking a React, TypeScript, and Next.js expert...",
    "resume_text": "Software engineer with 5 years experience building web apps..."
  }'`;

  const curlAtsBooster = `curl -X POST "${mcpEndpointUrl}" \\
  -H "Authorization: Bearer sk_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "tool": "ats_booster",
    "job_title": "Staff Fullstack Engineer",
    "job_description": "Looking for Bun, Hono, and SQLite proficiency...",
    "file_base64": "<BASE64_ENCODED_PDF_BYTES>"
  }'`;

  const jsExample = `const response = await fetch("${mcpEndpointUrl}", {
  method: "POST",
  headers: {
    "Authorization": "Bearer sk_live_your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    tool: "resume_tailor",
    job_title: "Product Engineer",
    job_description: "Build user-facing web experiences...",
    resume_text: "Experienced engineer with React skills..."
  }),
});

const data = await response.json();
console.log(data.result);`;

  return (
    <PageLayout id="api-docs-content" className="max-w-7xl">
      <PageHeader
        title="API and MCP docs"
        description="Call resume tailoring, ATS booster, and tracker endpoints from your own agents over MCP or REST."
        icon={BookOpen}
        badge={<ExperimentalBadge />}
        actions={
          <Link to="/api-keys">
            <Button variant="outline" size="sm" className="gap-1.5">
              <KeyRound className="size-4" aria-hidden="true" />
              Manage API keys
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Main Docs Content */}
        <div className="flex flex-col gap-8 lg:col-span-8">
          {/* Section 1: Authentication */}
          <section className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-border pb-3">
              <ShieldCheck className="size-4 text-violet-500" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-foreground">Authentication</h2>
            </div>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              Authenticate requests by passing your generated{" "}
              <code className="font-mono text-foreground">sk_live_...</code> API key in HTTP
              headers. Both Bearer tokens and custom header keys are supported.
            </p>
            <div className="space-y-2 rounded-xl border border-border bg-muted/40 p-4 font-mono text-xs text-foreground">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Header 1:</span>
                <code className="text-foreground">Authorization: Bearer sk_live_your_key</code>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Header 2:</span>
                <code className="text-foreground">x-api-key: sk_live_your_key</code>
              </div>
            </div>
          </section>

          {/* Section 2: MCP Integration Setup */}
          <section className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2.5">
                <Bot className="size-4 text-muted-foreground" aria-hidden="true" />
                <h2 className="text-sm font-semibold text-foreground">
                  Claude and Cursor integration
                </h2>
                <ExperimentalBadge text="Beta" />
              </div>
              <button
                type="button"
                onClick={() => copySnippet("claude-json", claudeConfigSnippet)}
                className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
              >
                {copiedCode === "claude-json" ? (
                  <Check className="size-3 text-emerald-500" aria-hidden="true" />
                ) : (
                  <Copy className="size-3" aria-hidden="true" />
                )}
                {copiedCode === "claude-json" ? "Copied config" : "Copy config"}
              </button>
            </div>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              Add JobCues as an MCP server in your{" "}
              <code className="font-mono text-foreground">claude_desktop_config.json</code> or
              Cursor IDE settings to let your AI assistant automatically tailor resumes and inject
              stealth ATS keywords.
            </p>
            <div className="overflow-hidden rounded-xl border border-border bg-muted/40 shadow-sm">
              <div className="flex items-center justify-between border-b border-border px-4 py-2 text-[11px] text-muted-foreground">
                <span className="font-mono">claude_desktop_config.json</span>
                <span className="font-mono text-[10px]">json</span>
              </div>
              <pre className="overflow-x-auto p-4 font-mono text-[11px] leading-relaxed text-foreground">
                {claudeConfigSnippet}
              </pre>
            </div>
          </section>

          {/* Section 3: Tool Reference */}
          <section className="flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <Code2 className="size-4 text-violet-500" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-foreground">Agent tools reference</h2>
            </div>

            {/* Tool 1: resume_tailor */}
            <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-[11px]">
                    POST /api/mcp
                  </Badge>
                  <code className="font-mono text-sm font-semibold text-foreground">
                    resume_tailor
                  </code>
                </div>
                <Badge variant="outline" className="text-[11px] text-muted-foreground">
                  AI resume rewriter
                </Badge>
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                Analyzes candidate resume against target job description and rewrites work
                experience, skills, and summary sections to maximize ATS relevance score.
              </p>

              <h4 className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Request parameters
              </h4>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-border bg-muted/40 font-semibold text-foreground">
                    <tr>
                      <th className="p-3">Field</th>
                      <th className="p-3">Type</th>
                      <th className="p-3">Required</th>
                      <th className="p-3">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border text-[13px] text-muted-foreground">
                    <tr>
                      <td className="p-3 font-mono font-semibold text-foreground">job_title</td>
                      <td className="p-3 font-mono text-xs">string</td>
                      <td className="p-3 font-mono text-xs font-semibold text-emerald-500">Yes</td>
                      <td className="p-3">Target Job Title</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-semibold text-foreground">
                        job_description
                      </td>
                      <td className="p-3 font-mono text-xs">string</td>
                      <td className="p-3 font-mono text-xs font-semibold text-emerald-500">Yes</td>
                      <td className="p-3">Target Job Description text</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-semibold text-foreground">resume_text</td>
                      <td className="p-3 font-mono text-xs">string</td>
                      <td className="p-3 font-mono text-xs text-muted-foreground">Optional</td>
                      <td className="p-3">Raw candidate resume text</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-semibold text-foreground">file_base64</td>
                      <td className="p-3 font-mono text-xs">string</td>
                      <td className="p-3 font-mono text-xs text-muted-foreground">Optional</td>
                      <td className="p-3">Base64 encoded original PDF or DOCX file</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-2 overflow-hidden rounded-xl border border-border bg-muted/40 shadow-sm">
                <div className="flex items-center justify-between border-b border-border px-4 py-2 text-[11px] text-muted-foreground">
                  <span className="font-mono">cURL example</span>
                  <button
                    type="button"
                    onClick={() => copySnippet("curl-tailor", curlResumeTailor)}
                    className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {copiedCode === "curl-tailor" ? (
                      <Check className="size-3 text-emerald-500" aria-hidden="true" />
                    ) : (
                      <Copy className="size-3" aria-hidden="true" />
                    )}
                    {copiedCode === "curl-tailor" ? "Copied" : "Copy cURL"}
                  </button>
                </div>
                <pre className="overflow-x-auto p-4 font-mono text-[11px] leading-relaxed text-foreground">
                  {curlResumeTailor}
                </pre>
              </div>
            </div>

            {/* Tool 2: ats_booster */}
            <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-[11px]">
                    POST /api/mcp
                  </Badge>
                  <code className="font-mono text-sm font-semibold text-foreground">
                    ats_booster
                  </code>
                </div>
                <Badge variant="outline" className="text-[11px] text-muted-foreground">
                  Stealth keyword injector
                </Badge>
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                Embeds high-converting job keywords invisibly into PDF resume (mode 3 text
                rendering) without altering visual layout or formatting.
              </p>

              <h4 className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Request parameters
              </h4>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-border bg-muted/40 font-semibold text-foreground">
                    <tr>
                      <th className="p-3">Field</th>
                      <th className="p-3">Type</th>
                      <th className="p-3">Required</th>
                      <th className="p-3">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border text-[13px] text-muted-foreground">
                    <tr>
                      <td className="p-3 font-mono font-semibold text-foreground">job_title</td>
                      <td className="p-3 font-mono text-xs">string</td>
                      <td className="p-3 font-mono text-xs font-semibold text-emerald-500">Yes</td>
                      <td className="p-3">Target Job Title</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-semibold text-foreground">
                        job_description
                      </td>
                      <td className="p-3 font-mono text-xs">string</td>
                      <td className="p-3 font-mono text-xs font-semibold text-emerald-500">Yes</td>
                      <td className="p-3">Target Job Description text</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono font-semibold text-foreground">file_base64</td>
                      <td className="p-3 font-mono text-xs">string</td>
                      <td className="p-3 font-mono text-xs font-semibold text-emerald-500">Yes</td>
                      <td className="p-3">Base64 encoded original PDF file</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-2 overflow-hidden rounded-xl border border-border bg-muted/40 shadow-sm">
                <div className="flex items-center justify-between border-b border-border px-4 py-2 text-[11px] text-muted-foreground">
                  <span className="font-mono">cURL example</span>
                  <button
                    type="button"
                    onClick={() => copySnippet("curl-ats", curlAtsBooster)}
                    className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {copiedCode === "curl-ats" ? (
                      <Check className="size-3 text-emerald-500" aria-hidden="true" />
                    ) : (
                      <Copy className="size-3" aria-hidden="true" />
                    )}
                    {copiedCode === "curl-ats" ? "Copied" : "Copy cURL"}
                  </button>
                </div>
                <pre className="overflow-x-auto p-4 font-mono text-[11px] leading-relaxed text-foreground">
                  {curlAtsBooster}
                </pre>
              </div>
            </div>
          </section>
        </div>

        {/* Right Sidebar */}
        <div className="flex flex-col gap-6 lg:col-span-4">
          <div className="sticky top-20 flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Sparkles className="size-4 text-violet-500" aria-hidden="true" />
                JavaScript SDK example
              </h3>
              <button
                type="button"
                onClick={() => copySnippet("js-example", jsExample)}
                className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
              >
                {copiedCode === "js-example" ? (
                  <Check className="size-3 text-emerald-500" aria-hidden="true" />
                ) : (
                  <Copy className="size-3" aria-hidden="true" />
                )}
                {copiedCode === "js-example" ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              Execute agent calls programmatically in Node.js, Bun, or browser environments:
            </p>

            <pre className="overflow-x-auto rounded-xl border border-border bg-muted/40 p-3.5 font-mono text-[11px] leading-relaxed text-foreground shadow-sm">
              {jsExample}
            </pre>

            <div className="mt-2 flex flex-col gap-2 border-t border-border pt-4">
              <h4 className="text-xs font-semibold text-foreground">Need API access?</h4>
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                Generate and manage secret API keys anytime in your dashboard.
              </p>
              <Link to="/api-keys" className="mt-1">
                <Button size="sm" className="w-full">
                  Go to API keys
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
